/**
 * ShadowModeBanner.js
 * 
 * A persistent banner shown when Platform Owner is in "View As" shadow mode.
 * This banner is ALWAYS visible and impossible to miss.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useShadowMode } from '../contexts/ShadowModeContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eye, LogOut, User, ShieldAlert, Clock } from 'lucide-react';

const ShadowModeBanner = () => {
  const { shadowSession, isInShadowMode, exitShadowMode, loading } = useShadowMode();
  const navigate = useNavigate();

  if (!isInShadowMode || !shadowSession) {
    return null;
  }

  const handleExit = async () => {
    try {
      await exitShadowMode();
      navigate('/platform-owner');
    } catch (err) {
      console.error('Failed to exit shadow mode:', err);
    }
  };

  const targetName = shadowSession.target_user_name || shadowSession.target_user_email || 'Unknown User';
  const isHost = shadowSession.target_is_host;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 shadow-lg"
      data-testid="shadow-mode-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Shadow mode info */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
              <Eye className="w-5 h-5 text-white animate-pulse" />
              <span className="text-white font-bold text-sm uppercase tracking-wide">
                Shadow Mode
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-white/90" />
              <span className="text-white font-medium">
                Viewing as: <strong>{targetName}</strong>
              </span>
            </div>
            
            <Badge className={`${isHost ? 'bg-blue-600' : 'bg-green-600'} text-white border-0`}>
              {isHost ? 'Host' : 'Renter/User'}
            </Badge>
          </div>

          {/* Right side - Exit button */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-white/80 text-xs">
              <ShieldAlert className="w-4 h-4" />
              <span>Admin: {shadowSession.admin_email}</span>
            </div>
            
            <Button
              onClick={handleExit}
              disabled={loading}
              className="bg-white text-red-600 hover:bg-red-50 font-bold shadow-md"
              size="sm"
              data-testid="exit-shadow-mode-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {loading ? 'Exiting...' : 'Exit Shadow Mode'}
            </Button>
          </div>
        </div>
        
        {/* Warning message */}
        <div className="text-center text-white/90 text-xs mt-1 pb-1">
          All actions in this session are being audited. This is a view-only support session.
        </div>
      </div>
    </div>
  );
};

export default ShadowModeBanner;
